import * as React from 'react';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import AddIcon from '@material-ui/icons/Add';
import RemoveIcon from '@material-ui/icons/Remove';
import Typography from '@material-ui/core/Typography';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormControl from '@material-ui/core/FormControl';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import MenuItem from '@material-ui/core/MenuItem';
import { Theme, withStyles, WithStyles } from '@material-ui/core/styles';
import FormInfo from '../../components/forms/FormInfo';
import { localizations } from '../../localization/localization';
import { Control, Form, Field, Fieldset, ModelState, Errors, actions as formActions } from 'react-redux-form';
import { renderDatePicker,
    renderTimePicker,
    renderCheckbox,
    renderNewTextFieldUpperCase,
    renderNumberField,
    renderRadioButton,
    renderMultilineTextField,
    renderNumberSlider,
    renderErrorInfoText,
    renderSelectWithTextLabel } from '../../common/forms/FieldCreators';
import { UnitModel, UnitType, AddUnitModel, UnitProperties } from '../UnitModels';
import nameof from '../../utils/nameof';
import { CitySuggestionDropDown } from '../../components/forms/CitySuggestionDropDown';
import FormItem from '../../components/forms/FormItem';
import { connect } from 'react-redux';
import WeeklyControls from '../../components/recurrence/WeeklyControls';
import DurationControls from '../../components/recurrence/DurationControls';
import { RecurrenceModel, RecurrenceType, weekdaySelected } from '../../common/models/Recurrence';
import { EditMode } from '../../common/models/CommonModels';
import moment from 'moment';
import { LoginState } from '../../login/loginStore';
import { UserRole } from '../../login/LoginModels';
import { actionCreators as companiesActions } from '../../admin/store/companiesStore';
import { RegisteredCompanyModel } from '../../admin/models/CompanyModels';

const styles = (theme: Theme) => ({
    root: {
        display: 'flex',
    },
    checkBox: {
        marginLeft: 0
    },
    timeSection: {
        marginTop: theme.spacing(4)
    }
});

interface FromInternalState {
    recurring: boolean;
    selectedRecurringType: "daily" | "weekly" | "monthly";
    isDistanceCheckEnabled: boolean;
}

interface AddNewUnitFormState {
    selectedType: UnitType
}

interface AddNewUnitFormProps {
    onCancel: () => void,
    onSubmit: (values: UnitModel) => void,
    resetTrailerIdentifier: () => void,
    isOpen: boolean
    formControl?: JSX.Element[] | JSX.Element;
    isEditMode: boolean;
    selectedRecurrenceLimit: string;
    newUnitForm: any,
    setFormValue: (model: string, value: any) => void;
    login: LoginState;
    requestRegisteredCompanies: () => void;
    companies: RegisteredCompanyModel[];
}

type FormProps = WithStyles<"root" | "checkBox" | "timeSection"> & FromInternalState & EditMode

class AddNewUnitForm extends React.Component<AddNewUnitFormProps & AddNewUnitFormState & FormProps, {}>{

    constructor(props: any) {
        super(props);

        this.handleSubmit = this.handleSubmit.bind(this);
    }

    public componentDidMount() {
        const { login, requestRegisteredCompanies } = this.props;

        if (login.role === UserRole.Admin) {
            requestRegisteredCompanies();
        }
    }

    public componentDidUpdate(prevProps: AddNewUnitFormProps) {
        const { login, isOpen, newUnitForm, companies, setFormValue } = this.props;

        if (login.role !== UserRole.Admin) {
            return;
        }

        if ((isOpen && !prevProps.isOpen || companies !== prevProps.companies) && !newUnitForm.companyId.value && !newUnitForm.createdBy.value) {
            const selectedCompanyId = sessionStorage.getItem('selectedCompanyId');
            const selectedCompany = companies.find(company => company.id === selectedCompanyId);
            const selectedUserId = sessionStorage.getItem('selectedUserId');
            const selectedUser = selectedCompany?.employees?.find(employee => employee.id === selectedUserId);

            if (selectedCompany && selectedUser) {
                setFormValue(`newUnit.${nameof<AddUnitModel>('companyId')}`, selectedCompany.id);
                setFormValue(`newUnit.${nameof<AddUnitModel>('createdBy')}`, selectedUser.id);
            }
        }

        if (newUnitForm.companyId.value !== prevProps.newUnitForm.companyId.value && prevProps.newUnitForm.companyId.value) {
            setFormValue(`newUnit.${nameof<AddUnitModel>('createdBy')}`, '');
        }
    }

    public render() {
        const { classes, isOpen, selectedType, newUnitForm, setFormValue, login, companies } = this.props;
        return (
            <Dialog open={isOpen}>
                <Form hideNativeErrors model="newUnit" onSubmit={this.handleSubmit} validators={{
                    '': {
                        hasDeparture: (vals) => {
                            return vals.departureCity && vals.departureCity.name && vals.departureCity.name.length > 0;
                        },
                        hasDestination: (vals) => {
                            return vals.destinationCity && vals.destinationCity.name && vals.destinationCity.name.length > 0;
                        },
                        isWeekdaySelected: (vals) => {
                            return !this.props.recurring || (this.props.recurring && weekdaySelected(vals.recurrence.weekdays));
                        },
                        detailsMaxLength: (vals) => {
                            return !vals.details || vals.details.length <= 1000;
                        },
                        distanceOutsideBounds: (vals) => {
                            return !this.props.isDistanceCheckEnabled || (Number(vals.searchDistance) >= 0 && Number(vals.searchDistance) <= 200);
                        },
                        readyTimeIsBeforeDeliveryTime: (vals) => {
                            if (vals.readyTime) {
                                return vals.readyTime.isBefore(vals.deliveryTime);
                            }
                            return false;
                        },
                        hasUser: (vals) => {
                            return !vals.companyId || vals.companyId && vals.createdBy;
                        }
                    },
                }}>
                    <DialogTitle style={{ textAlign: "center" }}>
                        <Typography variant="h4" paragraph>
                            {this.props.editMode === "add" ? localizations.units.reportNewUnit : null}
                            {this.props.editMode === "edit" ? localizations.units.editUnitTitle : null}
                            {this.props.editMode === "copy" ? localizations.units.actions.copy : null}
                        </Typography>
                    </DialogTitle>
                    <DialogContent>
                        <Grid item>
                            <Grid container direction="column" alignItems="center">
                                <FormInfo infoText={localizations.units.fillInUnitInformation} />
                                <FormItem>
                                    <FormItem>
                                        <Control
                                            model={`.${nameof<AddUnitModel>("departureCity")}`}
                                            component={CitySuggestionDropDown}
                                            required
                                            label={localizations.common.pointOfDeparture}
                                        />
                                    </FormItem>
                                    <Errors
                                        model="newUnit"
                                        show={{ touched: true, focus: false }}
                                        messages={{ hasDeparture: localizations.validation.required }}
                                        wrapper={renderErrorInfoText}
                                    />
                                    <FormItem>
                                        <Control
                                            model={`.${nameof<AddUnitModel>("destinationCity")}`}
                                            component={CitySuggestionDropDown}
                                            required
                                            label={localizations.common.pointOfArrival}
                                        />
                                    </FormItem>
                                    <Errors
                                        model="newUnit"
                                        show={{ touched: true, focus: false }}
                                        messages={{ hasDestination: localizations.validation.required }}
                                        wrapper={renderErrorInfoText}
                                    />
                                </FormItem>
                                <Grid className={classes.timeSection} container spacing={2}>
                                    <Grid container item xs={12}>
                                        <Grid item xs>
                                            <Control
                                                    model={`.${nameof<AddUnitModel>("readyTime")}`}
                                                    component={renderDatePicker}
                                                    required
                                                    label={localizations.units.readyForDeliveryDate}
                                                    />
                                        </Grid>
                                        <Grid item xs={1}>
                                                <IconButton
                                                aria-label={localizations.actions.next}
                                                onClick={() => setFormValue(`newUnit.${nameof<AddUnitModel>("readyTime")}`, moment(newUnitForm.readyTime.value).add(1, "days"))}>
                                                    <AddIcon />
                                                </IconButton>
                                        </Grid>
                                        <Grid item xs={1}>
                                                <IconButton
                                                aria-label={localizations.actions.previous}
                                                onClick={() => setFormValue(`newUnit.${nameof<AddUnitModel>("readyTime")}`, moment(newUnitForm.readyTime.value).subtract(1, "days"))}>
                                                    <RemoveIcon />
                                                </IconButton>
                                        </Grid>
                                    </Grid>
                                    <Grid container item xs={12}>
                                        <Grid item xs>
                                            <Control
                                                    model={`.${nameof<AddUnitModel>("readyTime")}`}
                                                    component={renderTimePicker}
                                                    required
                                                    label={localizations.units.readyForDeliveryTime}
                                                    />
                                        </Grid>
                                        <Grid item xs={1}>
                                                <IconButton
                                                aria-label={localizations.actions.next}
                                                onClick={() => setFormValue(`newUnit.${nameof<AddUnitModel>("readyTime")}`, moment(newUnitForm.readyTime.value).add(1, "hours"))}>
                                                    <AddIcon />
                                                </IconButton>
                                        </Grid>
                                        <Grid item xs={1}>
                                                <IconButton
                                                aria-label={localizations.actions.previous}
                                                onClick={() => setFormValue(`newUnit.${nameof<AddUnitModel>("readyTime")}`, moment(newUnitForm.readyTime.value).subtract(1, "hours"))}>
                                                    <RemoveIcon />
                                                </IconButton>
                                        </Grid>
                                    </Grid>
                                    <Grid container item xs={12}>
                                        <Grid item xs>
                                            <Control
                                                model={`.${nameof<AddUnitModel>("deliveryTime")}`}
                                                component={renderDatePicker}
                                                required
                                                label={localizations.units.timeofDeliveryDate}
                                                />
                                            </Grid>
                                        <Grid item xs={1}>
                                                <IconButton
                                                aria-label={localizations.actions.next}
                                                onClick={() => setFormValue(`newUnit.${nameof<AddUnitModel>("deliveryTime")}`, moment(newUnitForm.deliveryTime.value).add(1, "days"))}>
                                                    <AddIcon />
                                                </IconButton>
                                        </Grid>
                                        <Grid item xs={1}>
                                                <IconButton
                                                aria-label={localizations.actions.previous}
                                                onClick={() => setFormValue(`newUnit.${nameof<AddUnitModel>("deliveryTime")}`, moment(newUnitForm.deliveryTime.value).subtract(1, "days"))}>
                                                    <RemoveIcon />
                                                </IconButton>
                                        </Grid>
                                    </Grid>
                                    <Grid container item xs={12}>
                                        <Grid item xs>
                                            <Control
                                                model={`.${nameof<AddUnitModel>("deliveryTime")}`}
                                                component={renderTimePicker}
                                                required
                                                label={localizations.units.timeOfDelivery}
                                                />
                                        </Grid>
                                        <Grid item xs={1}>
                                                <IconButton
                                                aria-label={localizations.actions.next}
                                                onClick={() => setFormValue(`newUnit.${nameof<AddUnitModel>("deliveryTime")}`, moment(newUnitForm.deliveryTime.value).add(1, "hours"))}>
                                                    <AddIcon />
                                                </IconButton>
                                        </Grid>
                                        <Grid item xs={1}>
                                                <IconButton
                                                aria-label={localizations.actions.previous}
                                                onClick={() => setFormValue(`newUnit.${nameof<AddUnitModel>("deliveryTime")}`, moment(newUnitForm.deliveryTime.value).subtract(1, "hours"))}>
                                                    <RemoveIcon />
                                                </IconButton>
                                        </Grid>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Errors
                                            model="newUnit"
                                            show={{ touched: true, focus: false }}
                                            messages={{ readyTimeIsBeforeDeliveryTime: localizations.validation.endTimeAfterStartTime }}
                                            wrapper={renderErrorInfoText}
                                        />
                                    </Grid>
                                </Grid>
                                <FormItem>
                                    <FormItem>
                                        <Control.text
                                            model={`.${nameof<AddUnitModel>("contractPrice")}`}
                                            component={renderNumberField}
                                            label={localizations.units.contractPrice}
                                        />
                                    </FormItem>
                                </FormItem>
                                <div className={classes.root}>
                                    <FormItem>
                                        <FormControl>
                                            <Typography align="left" variant="body2">{localizations.common.type}</Typography>
                                            <Field model={`.${nameof<AddUnitModel>("trailerType")}`} dynamic={false}>
                                                <FormControlLabel
                                                    control={
                                                        <Control.radio
                                                            model={`.${nameof<AddUnitModel>("trailerType")}`}
                                                            component={renderRadioButton}
                                                            onChange={() => { this.props.resetTrailerIdentifier(); }}
                                                            value={UnitType.Unspecified}
                                                        />
                                                    }
                                                    label={localizations.unitTypes[UnitType.Unspecified]}
                                                />
                                                <FormControlLabel
                                                    control={
                                                        <Control.radio
                                                            model={`.${nameof<AddUnitModel>("trailerType")}`}
                                                            component={renderRadioButton}
                                                            value={UnitType.Trailer}
                                                        />
                                                    }
                                                    label={localizations.unitTypes[UnitType.Trailer]}
                                                />
                                                <FormControlLabel
                                                    control={
                                                        <Control.radio
                                                            model={`.${nameof<AddUnitModel>("trailerType")}`}
                                                            component={renderRadioButton}
                                                            value={UnitType.Container20}
                                                        />
                                                    }
                                                    label={localizations.unitTypes[UnitType.Container20]}
                                                />
                                                <FormControlLabel
                                                    control={
                                                        <Control.radio
                                                            model={`.${nameof<AddUnitModel>("trailerType")}`}
                                                            component={renderRadioButton}
                                                            value={UnitType.ContainerOver20}
                                                        />
                                                    }
                                                    label={localizations.unitTypes[UnitType.ContainerOver20]}
                                                />
                                            </Field>
                                        </FormControl>
                                    </FormItem>
                                </div>
                                {this.getContentByType(selectedType)}
                                <FormItem>
                                    <Typography align="left" variant="body2">{localizations.common.properties}</Typography>
                                    <table>
                                        <tbody>
                                            <tr>
                                                <td>
                                                    <FormControlLabel
                                                        className={classes.checkBox}
                                                        control={
                                                            <Control.checkbox
                                                                model={`.${nameof<AddUnitModel>("properties")}.${nameof<UnitProperties>("adr")}`}
                                                                component={renderCheckbox}
                                                            />
                                                        }
                                                        label={localizations.trucks.properties.adr}
                                                    />
                                                </td>
                                                <td>
                                                    <FormControlLabel
                                                        className={classes.checkBox}
                                                        control={
                                                            <Control.checkbox
                                                                model={`.${nameof<AddUnitModel>("properties")}.${nameof<UnitProperties>("explosive")}`}
                                                                component={renderCheckbox}
                                                            />
                                                        }
                                                        label={localizations.trucks.properties.explosive}
                                                    />
                                                </td>
                                                <td>
                                                    <FormControlLabel
                                                        className={classes.checkBox}
                                                        control={
                                                            <Control.checkbox
                                                                model={`.${nameof<AddUnitModel>("properties")}.${nameof<UnitProperties>("thermal")}`}
                                                                component={renderCheckbox}
                                                            />
                                                        }
                                                        label={localizations.trucks.properties.thermal}
                                                    />
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>
                                                    <FormControlLabel
                                                        className={classes.checkBox}
                                                        control={
                                                            <Control.checkbox
                                                                model={`.${nameof<AddUnitModel>("properties")}.${nameof<UnitProperties>("tank")}`}
                                                                component={renderCheckbox}
                                                            />
                                                        }
                                                        label={localizations.trucks.properties.tank}
                                                    />
                                                </td>
                                                <td>
                                                    <FormControlLabel
                                                        className={classes.checkBox}
                                                        control={
                                                            <Control.checkbox
                                                                model={`.${nameof<AddUnitModel>("properties")}.${nameof<UnitProperties>("glass")}`}
                                                                component={renderCheckbox}
                                                            />
                                                        }
                                                        label={localizations.trucks.properties.glass}
                                                    />
                                                </td>
                                                <td>
                                                    <FormControlLabel
                                                        className={classes.checkBox}
                                                        control={
                                                            <Control.checkbox
                                                                model={`.${nameof<AddUnitModel>("properties")}.${nameof<UnitProperties>("mega")}`}
                                                                component={renderCheckbox}
                                                            />
                                                        }
                                                        label={localizations.trucks.properties.mega}
                                                    />
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>
                                                    <FormControlLabel
                                                        className={classes.checkBox}
                                                        control={
                                                            <Control.checkbox
                                                                model={`.${nameof<AddUnitModel>("properties")}.${nameof<UnitProperties>("pieceGoods")}`}
                                                                component={renderCheckbox}
                                                            />
                                                        }
                                                        label={localizations.trucks.properties.pieceGoods}
                                                    />
                                                </td>
                                                <td>
                                                    <FormControlLabel
                                                        className={classes.checkBox}
                                                        control={
                                                            <Control.checkbox
                                                                model={`.${nameof<AddUnitModel>("properties")}.${nameof<UnitProperties>("bulkGoods")}`}
                                                                component={renderCheckbox}
                                                            />
                                                        }
                                                        label={localizations.trucks.properties.bulkGoods}
                                                    />
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </FormItem>
                                <FormItem>
                                    <FormControlLabel
                                        control={
                                            <Control.checkbox
                                                model={`.${nameof<AddUnitModel>("isDistanceCheckEnabled")}`}
                                                component={renderCheckbox}
                                                checked={this.props.isDistanceCheckEnabled}
                                                title={localizations.common.activateUnitCheckTooltip}
                                            />
                                        }
                                        label={localizations.common.activateDistanceCheck}
                                    />
                                </FormItem>
                                {this.props.isDistanceCheckEnabled ? this.getDistanceCheckControls() : null}
                                <FormItem>
                                    <Control.text
                                        model={`.${nameof<AddUnitModel>("details")}`}
                                        component={renderMultilineTextField}
                                        label={localizations.common.details}
                                    />
                                    <Errors
                                        model="newUnit"
                                        show={{ touched: true, focus: false }}
                                        messages={{ detailsMaxLength: localizations.validation.maxLength1000 }}
                                        wrapper={renderErrorInfoText}
                                    />
                                </FormItem>
                                <FormItem>
                                    <FormControlLabel
                                        control={
                                            <Control.checkbox
                                                model={`.${nameof<AddUnitModel>("recurrence")}.${nameof<RecurrenceModel>("recurring")}`}
                                                component={renderCheckbox}
                                                checked={this.props.recurring}
                                                disabled={this.props.editMode === "edit"}
                                            />
                                        }
                                        label={localizations.trucks.weeklyRecurringAdd}
                                    />
                                </FormItem>
                                {this.props.recurring ? this.getRecurringControls() : null}
                                {login.role === UserRole.Admin &&
                                <>
                                    <FormItem>
                                        <Control.text
                                            label={localizations.company.company}
                                            model={`.${nameof<AddUnitModel>("companyId")}`}
                                            component={renderSelectWithTextLabel} 
                                            id="addNewUnitForm-companyId-select"
                                            controlProps={{
                                                fullWidth: true
                                            }}
                                        >
                                            <MenuItem value="">{localizations.company.ownCompany}</MenuItem>
                                            {companies.map(company => <MenuItem key={company.id} value={company.id}>{company.name}</MenuItem>)}
                                        </Control.text>
                                    </FormItem>
                                    <FormItem>
                                        <Control.text
                                            label={localizations.common.organizer}
                                            model={`.${nameof<AddUnitModel>("createdBy")}`}
                                            component={renderSelectWithTextLabel} 
                                            id="addNewUnitForm-createdBy-select"
                                            controlProps={{
                                                fullWidth: true
                                            }}
                                        >
                                            <MenuItem value="">{localizations.common.user}</MenuItem>
                                            {companies.find(company => company.id === newUnitForm.companyId.value)?.employees?.map(user => <MenuItem key={user.id} value={user.id}>{`${user.firstName} ${user.lastName}`}</MenuItem>)}
                                        </Control.text>
                                        <Errors
                                            model="newUnit"
                                            show={{ touched: true, focus: false }}
                                            messages={{ hasUser: localizations.validation.required }}
                                            wrapper={renderErrorInfoText}
                                        />
                                    </FormItem>
                                </>
                                }
                                <DialogActions>
                                    <Grid item>
                                        <Grid container justify="center" spacing={4}>
                                            <Grid item>
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    onClick={this.props.onCancel}
                                                >
                                                    {localizations.actions.cancel}
                                                </Button>
                                            </Grid>
                                            <Grid item>
                                                <Button
                                                    type="submit"
                                                    variant="contained"
                                                    color="secondary"
                                                >
                                                    {localizations.actions.save}
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </DialogActions>
                            </Grid>
                        </Grid>
                    </DialogContent>
                </Form>
            </Dialog>
        );
    }

    private getContentByType(type: UnitType) {
        switch (type) {
            case UnitType.Trailer:
                return (
                    <FormItem>
                        <Control.text
                            model={`.${nameof<AddUnitModel>("trailerIdentifier")}`}
                            component={renderNewTextFieldUpperCase}
                            label={localizations.trucks.licensePlateNumber}
                        />
                    </FormItem>
                );
            case UnitType.Container20:
            case UnitType.ContainerOver20:
                return (
                    <FormItem>
                        <Control.text
                            model={`.${nameof<AddUnitModel>("trailerIdentifier")}`}
                            component={renderNewTextFieldUpperCase}
                            label={localizations.units.containerNumber}
                        />
                    </FormItem>
                );
            case UnitType.Unspecified:
                return null;
            default:
                // Make sure every unit type is handled.
                const exhaustiveCheck: never = type;
        }

        return null;
    }
    private getRecurringControls() {
        const { newUnitForm, setFormValue } = this.props;

        return (
            <FormItem>
                <FormItem>
                    <Fieldset model=".recurrence">
                        {this.getRecurringByType(this.props.selectedRecurringType)}
                        <DurationControls
                            modifying={this.props.editMode === "edit"}
                            selectedType={this.props.selectedRecurrenceLimit}
                            startDate={newUnitForm.recurrence.startDate.value}
                            setStartDate={(value: moment.Moment) => setFormValue(`newUnit.recurrence.${nameof<RecurrenceModel>("startDate")}`, value)} />
                    </Fieldset>
                </FormItem>
            </FormItem>
        );
    }

    private getRecurringByType(type: RecurrenceType) {
        return <WeeklyControls errorModelName="newUnit" disabled={this.props.editMode === "edit"} />;
    }

    private getDistanceCheckControls() {
        return (
            <FormItem>
                <Typography align="left" variant="body2">{localizations.common.searchDistance}</Typography>
                <br />
                <FormItem>
                    <Control
                        model={`.${nameof<AddUnitModel>("searchDistance")}`}
                        component={renderNumberSlider}
                    />
                </FormItem>
            </FormItem>
        );
    }

    private handleSubmit(values: UnitModel) {
        const { login, onSubmit } = this.props;

        if (login.role === UserRole.Admin
            && values.companyId
            && values.companyId !== login.companyId
            && values.createdBy
            && values.createdBy !== login.userId) {
            sessionStorage.setItem('selectedCompanyId', values.companyId);
            sessionStorage.setItem('selectedUserId', values.createdBy);
        }

        onSubmit(values);
    }
}

const mapStateToProps = ({ newUnit, forms, login, companies }: ModelState) => {
    return {
        selectedRecurringType: newUnit.recurrence.type,
        recurring: newUnit.recurrence.recurring,
        selectedType: newUnit.trailerType,
        selectedRecurrenceLimit: forms.newUnit.recurrence.endDateMode.value,
        isDistanceCheckEnabled: newUnit.isDistanceCheckEnabled,
        newUnitForm: forms.newUnit,
        login,
        companies: companies.data,
    }
}

const mapDispatchToProps = (dispatch: any) => {
    return {
      setFormValue: (model: string, value: any) => dispatch(formActions.change(model, value)),
      requestRegisteredCompanies: () => dispatch(companiesActions.requestRegisteredCompanies()),
    }
}

export default withStyles(styles)(connect<any, any, any, AddNewUnitFormProps>(mapStateToProps, mapDispatchToProps)(AddNewUnitForm));





// WEBPACK FOOTER //
// ./src/units/forms/AddNewUnitForm.tsx